import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { prefersReducedMotion, startsBelowTheFold } from './useReveal'

export function CountUp({
  value,
  duration = 1400,
  className,
}: {
  value: number
  duration?: number
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [shown, setShown] = useState(value)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof window.IntersectionObserver !== 'function') return
    if (prefersReducedMotion() || !startsBelowTheFold(element)) return
    let frame = 0
    setShown(0)
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        const start = performance.now()
        const step = (now: number) => {
          const progress = Math.min(1, (now - start) / duration)
          const eased = 1 - Math.pow(1 - progress, 3)
          setShown(Math.round(eased * value))
          if (progress < 1) frame = window.requestAnimationFrame(step)
        }
        frame = window.requestAnimationFrame(step)
      },
      { rootMargin: '0px 0px -10% 0px' },
    )
    observer.observe(element)
    return () => {
      observer.disconnect()
      if (frame) window.cancelAnimationFrame(frame)
      setShown(value)
    }
  }, [value, duration])

  return (
    <span ref={ref} className={cn('tabular-nums', className)}>
      {shown}
    </span>
  )
}
